// src/lib/geste-fait.js — CE QUE LE CLIC A DONNÉ, EN UN TOUR DE FIL.
//
// ══ POURQUOI (décision Dimitri, 2026-09-15) ════════════════════════════════
//
//   « Le "oui" doit devenir le clic, et il doit pouvoir me dire "c'est fait"
//     en ayant LU la réponse. »
//
// Jusqu'ici, `executerGeste` rendait son résultat à la carte, la carte
// affichait « ✓ C'est fait », et ça s'arrêtait là. Au tour suivant, l'opérateur
// n'en savait RIEN : il pouvait reproposer le même geste, ou répondre « je ne
// sais pas si ça a été fait » trente secondes après l'avoir fait faire. Le fil
// portait la conversation, jamais ses conséquences.
//
// Ce module compose le TOUR qui manque — celui qui dit ce qui vient d'être
// fait, et qui remonte au serveur avec le reste du fil.
//
// ══ CE QUI ENTRE DANS CE TOUR, ET SURTOUT CE QUI N'Y ENTRE PAS ═════════════
//
// ⚠️ JAMAIS LES ARGUMENTS. Un geste de la vague 9 porte le CORPS d'un message
// au voyageur (jusqu'à 4 000 caractères). Le renvoyer au modèle au tour suivant
// serait lui remettre sous les yeux un texte de tiers qu'il n'a pas le droit de
// relire — et ferait grossir le prompt de tout ce qu'on vient d'envoyer. Le
// tour dit CE QUI a été fait, jamais AVEC QUOI.
//
// ⚠️ LE TITRE VIENT DU REGISTRE, comme sur la carte. Un titre composé par le
// modèle ferait dire au fil autre chose que ce que l'action a fait — et c'est
// cette phrase-là que l'opérateur relira pour répondre « c'est fait ».
// Fail-closed : un levier que le registre ne connaît pas ne produit AUCUN tour
// (il ne s'affiche pas, il ne remonte pas). Mieux vaut un silence qu'une ligne
// « fait » dont personne ne peut nommer l'objet.
//
// ⚠️ ET L'ISSUE N'EST PAS TOUJOURS « FAIT ». `executerGeste` distingue trois
// natures d'échec, dont « on ne sait pas si c'est parti » : les aplatir en un
// seul « raté » ferait perdre exactement l'information qui compte après un
// clic. Elles voyagent telles quelles.

import { lireLevier } from "./operateur-leviers";
// L'empreinte — une chaîne opaque qui dit QUEL geste, sans dire avec quoi.
// Elle voyage avec le compte rendu pour que le serveur ne repropose pas ce qui
// vient d'être fait. Voir `lib/geste-empreinte.js`.
import { empreinteDeGeste } from "./geste-empreinte";

// ⚠️ RECOPIÉE de `lib/geste-resume.js::MAX_CARS_RESUME` — CRA/webpack refuse
// les imports hors de `src/`. L'égalité (constante ET comportement) est
// verrouillée par `src/lib/geste-fait.test.js`, qui compare les deux sur un
// corpus : un miroir qui diverge tronquerait autrement que la source.
export const MAX_CARS_RESUME = 900;

/** Les quatre issues d'un geste cliqué. Liste fermée — rien d'autre n'entre. */
export const ISSUES = ["fait", "refuse", "incertain", "annule"];

/**
 * Borne un résumé composé serveur. Miroir de `lib/geste-resume.js`.
 * Rend `null` si rien de présentable — le tour n'affiche alors pas de suite.
 */
export function bornerResume(texte) {
  if (typeof texte !== "string") return null;
  const t = texte.trim().replace(/\s+/g, " ");
  if (!t) return null;
  return t.length > MAX_CARS_RESUME ? `${t.slice(0, MAX_CARS_RESUME - 1)}…` : t;
}

/**
 * L'issue d'un retour d'`executerGeste`.
 *
 * ⚠️ DEUX ÉTAPES VALENT « INCERTAIN », et c'est le point : `levier_incertain`
 * (le réseau a lâché pendant l'appel) et `confirmation` (le geste EST parti,
 * seule sa trace a manqué). Les classer « refusé » ferait dire au fil « rien
 * n'a été fait » sur un geste qui est peut-être — ou sûrement — parti. C'est
 * la pire phrase possible : elle pousse à recliquer.
 */
export function issueDe(r) {
  if (r && r.ok === true) return "fait";
  const etape = r && typeof r.etape === "string" ? r.etape : "";
  if (etape === "levier_incertain" || etape === "confirmation")
    return "incertain";
  return "refuse";
}

const MARQUES = { fait: "✓", refuse: "✗", incertain: "?", annule: "—" };

// ── POURQUOI UN ÉCHEC N'EMPORTE PAS SA PHRASE ──────────────────────────────
//
// ⚠️ CORRIGÉ LE 2026-09-15 (revue adverse #1432, mineur 1). Le résumé d'un
// geste raté était `r.message` — c'est-à-dire la phrase du SERVEUR DU LEVIER,
// reprise telle quelle. Elle est faite pour l'écran, et elle peut nommer :
// « la réponse à chloe@… a déjà été envoyée », « le voyageur Martin… », une
// URL. Ce texte partait ensuite dans le fil, donc chez Claude, à CHAQUE tour.
//
// Un compte rendu n'a pas besoin de la phrase : il a besoin de l'ÉTAT. On rend
// donc un libellé FERMÉ, choisi par l'étape — le détail complet reste sous les
// yeux de Dimitri, sur la carte, qui affiche `echec.message` comme avant.
const LIBELLES_ECHEC = {
  levier: "le levier n'est plus proposable",
  // ⚠️ « CE GESTE », PAS « SA VAGUE » (23/09) : ce libellé part dans le fil,
  // donc sous les yeux de Dimitri et dans le prompt. Nommer la vague désigne
  // un ENSEMBLE fermé, alors qu'ils s'ouvrent un par un.
  vague: "ce geste n'est pas encore ouvert",
  arguments: "un argument n'était pas autorisé",
  choix: "un choix manquait sur la carte",
  texte: "le texte était vide ou trop long",
  apercu: "la valeur actuelle n'avait pas été lue",
  reclamation: "déjà pris en charge ailleurs",
  session: "la session avait expiré",
  levier_refuse: "le serveur a refusé",
  levier_incertain: "le geste est parti ou non, on ne sait pas",
  confirmation: "le geste est parti, mais sa trace a manqué",
};

/** La phrase du tour, telle qu'elle s'affiche dans le fil. */
function _texteDuTour(titre, issue, resume) {
  const m = MARQUES[issue] || "—";
  if (issue === "fait") return `${m} ${titre}${resume ? ` — ${resume}` : ""}`;
  const suite =
    issue === "incertain"
      ? "on ne sait pas si c'est parti"
      : issue === "annule"
        ? "annulé, rien n'a été fait"
        : "non fait";
  return `${m} ${titre} — ${suite}${resume ? ` : ${resume}` : ""}`;
}

/**
 * Le tour de fil d'un geste qui vient d'être cliqué.
 *
 * @param {object} geste — la carte (son `levier` fait foi)
 * @param {object} r — ce qu'a rendu `executerGeste`
 * @returns {{texte:string, fait:object}|null} — `null` si le levier est inconnu
 */
export function composerFait(geste, r) {
  const nom = geste && typeof geste.levier === "string" ? geste.levier : "";
  const levier = nom ? lireLevier(nom) : null;
  // Fail-closed : hors registre, pas de tour. Voir l'en-tête.
  if (!levier) return null;
  const issue = issueDe(r);
  // Pour un SUCCÈS, le résumé est celui que le serveur a composé pour être dit
  // (`lib/geste-resume.js`) ; pour un ÉCHEC, un libellé fermé — jamais la
  // phrase du serveur, qui peut nommer quelqu'un (voir `LIBELLES_ECHEC`).
  const etape = r && typeof r.etape === "string" ? r.etape : null;
  const resume = bornerResume(
    issue === "fait"
      ? r && r.resultat && r.resultat.resume
      : LIBELLES_ECHEC[etape] || null,
  );
  const fait = {
    levier: nom,
    titre: levier.titre,
    issue,
    // Où ça s'est arrêté. `null` quand tout s'est bien passé.
    etape,
    resume,
    empreinte: empreinteDeGeste(geste),
  };
  return { texte: _texteDuTour(levier.titre, issue, resume), fait };
}

/**
 * Le tour d'un geste ANNULÉ — Dimitri a dit « non » à une carte en attente.
 * Rien n'est parti ; le tour existe pour que l'opérateur ne la repropose pas.
 */
export function composerAnnulation(geste, motif) {
  const nom = geste && typeof geste.levier === "string" ? geste.levier : "";
  const levier = nom ? lireLevier(nom) : null;
  if (!levier) return null;
  // ⚠️ `motif` est écrit DANS LE CODE (« vous avez dit non »), jamais rendu par
  // un serveur ni par le modèle — même règle que `LIBELLES_ECHEC`. Le jour où
  // il viendrait d'ailleurs, il faudrait le fermer comme eux.
  const resume = bornerResume(motif);
  const fait = {
    levier: nom,
    titre: levier.titre,
    issue: "annule",
    etape: null,
    resume,
    empreinte: empreinteDeGeste(geste),
  };
  return { texte: _texteDuTour(levier.titre, "annule", resume), fait };
}
